import {
  BarChart as BarChartIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
  DarkModeRounded as DarkModeRoundedIcon,
  Dns as DnsIcon,
  ExitToApp as ExitToAppIcon,
  Group as GroupIcon,
  LightModeRounded as LightModeRoundedIcon,
  LinkedIn as LinkedInIcon,
  LockReset as LockResetIcon,
  Mail as MailIcon,
  Menu as MenuIcon,
  MoreVertRounded as MoreVertRoundedIcon,
  Router as RouterIcon,
  SpaceDashboard as SpaceDashboardIcon,
  Storage as StorageIcon,
  Terminal as TerminalIcon,
  X as XIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import AccessibilityPdf from '@/assets/Accessibility-Cityeye_v2.0.pdf';
import LogoA2AColor from '@/assets/logocolor.svg';
import LogoA2AWhite from '@/assets/logowhite.svg';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { usePermission } from '@/hooks/usePermission';
import { ROUTES } from '@/router/routes';
import { mqttService } from '@/services/mqttService';
import { queryClient } from '@/services/queryClient';
import { useAuthStore } from '@/store/authStore';
import { useMqttStore } from '@/store/mqttStore';
import { useThemeStore } from '@/store/themeStore';

import {
  APPBAR_HEIGHT,
  ContentWrapper,
  MainContent,
  Root,
  SIDEBAR_WIDTH,
  StyledAppBar,
  StyledDrawer,
} from './AppLayout.styles';

interface NavItemPermission {
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete';
  scope?: 'own' | 'any';
}

interface NavItem {
  label: string;
  icon: JSX.Element;
  path: string;
  /** If set, the item is shown only when the current user satisfies this permission check. */
  permission?: NavItemPermission;
}

const ConsoleNavIcon = (): JSX.Element => {
  const unreadCount = useMqttStore((state) => state.unreadCount);
  return (
    <Badge badgeContent={unreadCount || undefined} color="error" max={99}>
      <TerminalIcon />
    </Badge>
  );
};

const NAV_ITEMS: NavItem[] = [
  { label: 'Dispositivi', icon: <StorageIcon />, path: ROUTES.DEVICES },
  { label: 'Applicazioni', icon: <SpaceDashboardIcon />, path: ROUTES.APPLICATIONS },
  { label: 'Gateway', icon: <RouterIcon />, path: ROUTES.GATEWAYS },
  { label: 'Multicast', icon: <DnsIcon />, path: ROUTES.MULTICAST },
  { label: 'Utenti', icon: <GroupIcon />, path: ROUTES.USERS, permission: { resource: 'user', action: 'read', scope: 'any' } },
  { label: 'Console', icon: <ConsoleNavIcon />, path: ROUTES.CONSOLE },
  { label: 'Statistiche', icon: <BarChartIcon />, path: ROUTES.STATISTICS },
];

const getInitials = (username: string): string => {
  const parts = username.split(/[\s._@-]/);
  if (parts.length >= 2 && parts[1].length > 0) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return username.slice(0, 2).toUpperCase();
};

const ProfileBadge = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, clearUser } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    setAnchorEl(null);
    clearUser();
    queryClient.clear();
    navigate(ROUTES.LOGIN);
  };

  const initials = user ? getInitials(user.username) : '?';

  return (
    <>
      <Button
        id="profile-menu-button"
        variant="text"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ textTransform: 'unset', color: 'inherit' }}
        aria-label="menu utente"
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? 'profile-menu' : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography component="div" variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {user?.username}
            </Typography>
            <Typography component="div" variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {user?.role}
            </Typography>
          </Box>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {initials}
          </Avatar>
          <MoreVertRoundedIcon fontSize="small" />
        </Box>
      </Button>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{ top: '8px' }}
        slotProps={{ list: { 'aria-labelledby': 'profile-menu-button' } }}
      >
        <MenuItem onClick={() => navigate(ROUTES.PROFILE)}>
          <ListItemIcon><ChevronRightRoundedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Profilo</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}>
          <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Cambia password</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><ExitToAppIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const AppFooter = (): JSX.Element => {
  const { mode } = useThemeStore();
  const bgColor = mode === 'light' ? '#E9EFF5' : '#2d363f';

  return (
    <Box component="footer" sx={{ bgcolor: bgColor }}>
      <Container maxWidth="xl">
        <Stack
          sx={{ justifyContent: 'space-between' }}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2, md: 4 }}
        >
          <Box sx={{ py: 2 }}>
            <Box sx={{ width: '120px' }}>
              <Box
                component="img"
                sx={{ height: '50px' }}
                alt="A2A Life Company logo"
                src={mode === 'light' ? LogoA2AColor : LogoA2AWhite}
              />
            </Box>
          </Box>

          <Box sx={{ py: 2 }}>
            <Typography variant="body2">
              <strong>Bergamo</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Via Suardi, 26
            </Typography>
          </Box>

          <Box sx={{ py: 2 }}>
            <Typography variant="body2">
              <strong>Brescia</strong>
            </Typography>
            <Typography variant="body2">Via A. Lamarmora, 230</Typography>
          </Box>

          <Box sx={{ py: 2 }}>
            <Typography variant="body2">
              <strong>Cremona</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Via dell&apos;Innovazione Digitale, 8
            </Typography>
          </Box>

          <Box sx={{ py: 2 }}>
            <Typography variant="body2">
              <strong>Milano</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Via Olgettina, 25
            </Typography>
          </Box>

          <Box sx={{ py: 2 }}>
            <Typography
              variant="h6"
              sx={{ textTransform: 'uppercase', fontWeight: 700 }}
            >
              Contatti
            </Typography>
            <Link href="mailto:info@a2asmartcity.it" variant="body2" color="inherit">
              info@a2asmartcity.it
            </Link>
            <br />
            <Link
              href="https://www.a2asmartcity.it/contatti/"
              variant="body2"
              color="inherit"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contattaci
            </Link>
          </Box>
        </Stack>

        <Stack sx={{ justifyContent: 'center', alignItems: 'center' }} direction="column">
          <ButtonGroup size="small">
            <Button
              variant="text"
              aria-label="twitter"
              href="https://twitter.com/A2ASmartCity"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
            >
              <XIcon />
            </Button>
            <Button
              variant="text"
              aria-label="linkedin"
              href="https://www.linkedin.com/showcase/a2a-smart-city/"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
            >
              <LinkedInIcon />
            </Button>
            <Button
              variant="text"
              aria-label="youtube"
              href="https://www.youtube.com/channel/UCHczlhVciqpwLdUweQTJXJQ"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
            >
              <YouTubeIcon />
            </Button>
            <Button
              variant="text"
              aria-label="email"
              href="mailto:info@a2asmartcity.it"
              color="inherit"
            >
              <MailIcon />
            </Button>
          </ButtonGroup>

          <Typography variant="body2">
            <small>
              Copyright &copy;{new Date().getFullYear()} A2A Smart city - P.I. 02159020177 -{' '}
              www.a2asmartcity.it
            </small>
          </Typography>

          <Typography variant="body2">
            <small>
              <Link href={AccessibilityPdf} color="inherit">
                Documento di accessibilit&agrave;
              </Link>
            </small>
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export const AppLayout = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useThemeStore();
  const { can } = usePermission();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useLocalStorage('sidebar-collapsed', false);

  useEffect(() => {
    const { subscriptions } = useMqttStore.getState();
    if (subscriptions.length > 0) {
      mqttService.connect();
      subscriptions.forEach((sub) => mqttService.addTopics(sub.topics));
    }
  }, []);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.permission || can(item.permission.resource, item.permission.action, item.permission.scope),
  );

  const isCollapsed = collapsed && !isMobile;

  const drawerContent = (
    <Box component="nav" aria-label="Navigazione principale" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <List dense sx={{ flexGrow: 1, p: 0 }}>
        {visibleItems.map((item) => {
          const isSelected = location.pathname.startsWith(item.path);
          return (
            <Tooltip key={item.path} title={isCollapsed ? item.label : ''} placement="right">
              <ListItemButton
                selected={isSelected}
                aria-current={isSelected ? 'page' : undefined}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  py: 2,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2,
                  borderRadius: 0,
                  '&.Mui-selected': {
                    bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(10, 140, 215, 0.08)' : 'rgba(10, 140, 215, 0.16)',
                    borderRight: '4px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(10, 140, 215, 0.12)' : 'rgba(10, 140, 215, 0.24)',
                    }
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 0 : 2,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
      {!isMobile && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-end', p: 1 }}>
            <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
              {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Root>
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: 1,
          height: 1,
          overflow: 'hidden',
          zIndex: 9999,
        }}
        onFocus={(e) => {
          Object.assign(e.currentTarget.style, {
            left: '50%',
            top: '8px',
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
            transform: 'translateX(-50%)',
            padding: '8px 16px',
            background: '#fff',
            color: '#000',
            fontWeight: 'bold',
            textDecoration: 'none',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          });
        }}
        onBlur={(e) => {
          Object.assign(e.currentTarget.style, {
            left: '-9999px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            transform: '',
          });
        }}
      >
        Vai al contenuto principale
      </a>
      <StyledAppBar position="fixed" elevation={1}>
        <Toolbar>
          {(isMobile || !collapsed) && (
            <IconButton
              color="inherit"
              aria-label={isMobile ? 'apri menu' : 'collassa menu'}
              edge="start"
              onClick={isMobile ? () => setMobileOpen(true) : () => setCollapsed(true)}
              sx={{ mr: 1, display: isMobile ? 'inline-flex' : { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {(!collapsed || isMobile) && (
            <Link
              component="button"
              onClick={() => navigate(ROUTES.DEVICES)}
              underline="none"
              aria-label="PTNetSuite - vai alla home"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography
                variant="h6"
                color="primary"
                sx={{ letterSpacing: '-0.5px', fontWeight: 700 }}
              >
                PT
              </Typography>
              <Typography
                variant="h6"
                color="text.primary"
                sx={{ letterSpacing: '-0.5px', fontWeight: 400 }}
              >
                NetSuite
              </Typography>
            </Link>
          )}

          {collapsed && !isMobile && (
            <Link
              component="button"
              onClick={() => navigate(ROUTES.DEVICES)}
              underline="none"
              aria-label="PTNetSuite - vai alla home"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography
                variant="h6"
                color="primary"
                sx={{ letterSpacing: '-0.5px', fontWeight: 700 }}
              >
                PT
              </Typography>
            </Link>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" sx={{ alignItems: 'center' }} spacing={0.5}>
            <Tooltip title={mode === 'light' ? 'Tema scuro' : 'Tema chiaro'}>
              <IconButton
                onClick={toggleMode}
                color="primary"
                aria-label="cambia tema"
                size="small"
              >
                {mode === 'light' ? (
                  <DarkModeRoundedIcon fontSize="small" />
                ) : (
                  <LightModeRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

            <ProfileBadge />
          </Stack>
        </Toolbar>
      </StyledAppBar>

      <ContentWrapper>
        {!isMobile && (
          <StyledDrawer variant="permanent" collapsed={collapsed}>
            {drawerContent}
          </StyledDrawer>
        )}

        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: SIDEBAR_WIDTH,
                boxSizing: 'border-box',
                marginTop: `${APPBAR_HEIGHT}px`,
                height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}

        <MainContent id="main-content">
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>}>
            <Outlet />
          </Suspense>
        </MainContent>
      </ContentWrapper>

      <AppFooter />
    </Root>
  );
};
